import { Injectable } from '@angular/core';
import * as _ from "lodash-es";
import { AppConstants } from "@core/constants";

declare let Bokeh_v0123: any;

let BORDER_COLOR: string = '#444444';
let GRID_LINE_COLOR: string = '#444444';
let CHART_BORDER_FILL_COLOR: string = '#282929';
let CHART_BACKGROUND_FILL_COLOR: string = '#111111';
let LEGEND_BACKGROUND_FILL_ALPHA: number = 0.75;
let LABEL_TEXT_COLOR: string = '#aaa';
let CHART_TITLE_COLOR: string = '#ddd';
let LEGEND_BORDER_WIDTH: number = 0;


@Injectable({
  providedIn: 'root'
})
export class PerformanceDevelopmentChartPreviewService {
  public plottingAPI = Bokeh_v0123.Plotting;

  public updateColorTheme(currentTheme: string, isPdfPreview = false): void {
    if (currentTheme == AppConstants.THEME.LIGHT) {
      BORDER_COLOR = '#BBBBBB';
      GRID_LINE_COLOR = '#BBBBBB';
      CHART_BORDER_FILL_COLOR = isPdfPreview ? '#FFF' : '#FBFBFB';
      CHART_BACKGROUND_FILL_COLOR = isPdfPreview ? '#FFF' : '#FBFBFB';
      LABEL_TEXT_COLOR = '#444444';
      CHART_TITLE_COLOR = '#222222';
    } else {
      BORDER_COLOR = '#444444';
      GRID_LINE_COLOR = '#444444';
      CHART_BORDER_FILL_COLOR = isPdfPreview ? '#FFF' : '#282929';
      CHART_BACKGROUND_FILL_COLOR = isPdfPreview ? '#FFF' : '#111111';
      LABEL_TEXT_COLOR = '#aaa';
      CHART_TITLE_COLOR = '#ddd';
    }
  }

  public generatePerformanceDevelopmentChart(plot: any, elementId: any, testId: any, isPdf: boolean = false,): void {
    this.updateColorTheme(localStorage.getItem('theme')!);
    if (!plot) {
      return;
    }

    const TOOLS: boolean | string = isPdf ? false : 'pan,box_zoom,reset';
    //// let line_colors = ['blue', 'red', 'green', 'magenta', 'lightblue', 'black'];

    let lineObject: { y_range_name: any } | undefined;

    let plotData: any;
    plotData = {
      plot_width: 1160,
      plot_height: 380,
      tools: TOOLS,
      x_axis_type: plot.x_axis_type,
      background_fill_color: CHART_BACKGROUND_FILL_COLOR,
      border_fill_color: CHART_BORDER_FILL_COLOR,
      outline_line_color: BORDER_COLOR,
      sizing_mode: 'scale_width',
      toolbar_location: isPdf ? null : 'above'
    };

    this.handleXAxisRange(plotData, plot, testId); // IAT-2354 let x axis auto scaling
    this.handleYAxisRange(plotData, plot, testId);

    if (plot.extra_y_ranges) {
      plotData.extra_y_ranges = this.createExtraRange(plot.extra_y_ranges);
    }

    const y_extra_list: string[] = [];
    for (let key in plot.extra_y_ranges) y_extra_list.push(key.toString());

    // Switch to categorical x-axis if needed (to eliminate gaps)
    if (plot.use_categorical_axis && plot.date_categories && plot.date_categories.length > 0) {
      plotData.x_axis_type = null; // Override datetime axis
      plotData.x_range = new Bokeh_v0123.FactorRange({ factors: plot.date_categories });
    }

    let chart = this.plottingAPI.figure(plotData);

    // Use custom ticks for x-axis if available
    if (plot.use_custom_x_axis && plot.custom_x_ticks && plot.x_labels_sorted) {
      // Create a custom ticker with even spacing
      const ticker = new Bokeh_v0123.FixedTicker({
        ticks: plot.custom_x_ticks
      });

      // Create a custom formatter using the sorted date strings
      const formatter = new Bokeh_v0123.FuncTickFormatter({
        args: { labels: plot.x_labels_sorted },
        code: `
      const index = Math.round(tick);
      if (index >= 0 && index < labels.length) {
        return labels[index];
      }
      return "";
    `
      });

      // Apply the custom ticker and formatter to the x-axis
      chart.xaxis.ticker = ticker;
      chart.xaxis.formatter = formatter;
    }

    // chart.yaxis.formatter = new Bokeh_v0123.FuncTickFormatter({
    //   code: `return convertNumberToString(tick)`,
    // });

    this.drawPerformanceDevelopment(plot, lineObject, chart, y_extra_list);

    if (plot.layouts && plot.layouts.length > 0) {
      _.each(plot.layouts, (res): void => {
        chart = this.createLayout(res, chart);
      });
    }

    // Set title properties
    if (chart.title) {
      chart.title.text = plot['title_text'] || '';
      chart.title.align = 'center';
      chart.title.text_font_size = '12pt';
      chart.title.text_color = CHART_TITLE_COLOR;
    }

// Legend Styling
    if (chart._legend) {
      chart._legend.location = 'top_left';
      chart._legend.background_fill_color = CHART_BACKGROUND_FILL_COLOR;
      chart._legend.border_line_width = LEGEND_BORDER_WIDTH;
      chart._legend.background_fill_alpha = LEGEND_BACKGROUND_FILL_ALPHA;
      chart._legend.label_text_color = LABEL_TEXT_COLOR;
    }

// Y axis styling
    if (chart.yaxis) {
      chart.yaxis.axis_label = plot.y_axis_label || '';
      chart.yaxis.axis_label_text_font_size = '10pt';
      chart.yaxis.axis_label_text_font_style = 'normal';
      chart.yaxis.axis_label_text_color = LABEL_TEXT_COLOR;
      chart.yaxis.major_label_text_color = LABEL_TEXT_COLOR;
    }

// X axis styling
    if (chart.xaxis) {
      chart.xaxis.axis_label_text_font_size = '10pt';
      chart.xaxis.axis_label_text_font_style = 'normal';
      chart.xaxis.axis_label_text_color = LABEL_TEXT_COLOR;
      chart.xaxis.major_label_text_color = LABEL_TEXT_COLOR;
    }

// Right axis styling
    if (chart.right && Array.isArray(chart.right)) {
      chart.right.forEach((right: any) => {
        if (right && right.attributes) {
          right.attributes.axis_label_text_font_size = '10pt';
          right.attributes.axis_label_text_font_style = 'normal';
          right.attributes.axis_label_text_color = LABEL_TEXT_COLOR;
          right.attributes.major_label_text_color = LABEL_TEXT_COLOR;
        }
      });
    }

// Left axis styling
    if (chart.left && Array.isArray(chart.left)) {
      chart.left.forEach((left: any) => {
        if (left && left.attributes) {
          left.attributes.axis_label_text_font_style = 'normal';
          left.attributes.axis_label_text_font_size = '10pt';
          left.attributes.axis_label_text_color = LABEL_TEXT_COLOR;
          left.attributes.major_label_text_color = LABEL_TEXT_COLOR;
        }
      });
    }

// Grid Styling
    if (chart.xgrid) chart.xgrid.grid_line_color = GRID_LINE_COLOR;
    if (chart.ygrid) chart.ygrid.grid_line_color = GRID_LINE_COLOR;

    this.plottingAPI.show(chart, elementId);
  }

  private handleXAxisRange(plotData: any, plot: any, testId: any): void {
    let xVal: any = localStorage.getItem('PDsettings_' + testId);
    if (xVal) {
      xVal = JSON.parse(xVal);
      plotData.x_range = new Bokeh_v0123.Range1d({
        start: new Date(xVal.plot_range[0]).getTime(),
        end: new Date(xVal.plot_range[1]).getTime(),
      });
    } else {
      if (plot.is_one_day) {
        plotData.x_range = new Bokeh_v0123.Range1d({
          start: new Date(plot.x_range.plot_range[0]).getTime(),
          end: new Date(plot.x_range.plot_range[1]).getTime(),
        });
      }
    }
  }

  private handleYAxisRange(plotData: any, plot: any, testId: any): void {
    if (plot.y_range) {
      plotData.y_range = new Bokeh_v0123.Range1d({
        start: plot.y_range.plot_range[0],
        end: plot.y_range.plot_range[1],
      });
    }
  }

  public createExtraRange(range: any) {
    let extraRangeList: any = {};
    for (let range_name in range) {
      if (range.hasOwnProperty(range_name)) {
        extraRangeList[range_name] = new Bokeh_v0123.Range1d({
          start: range[range_name].plot_range[0],
          end: range[range_name].plot_range[1],
          bounds: range[range_name].bounds,
        });
      }
    }

    return extraRangeList;
  }

  private drawPerformanceDevelopment(plot: any, lineObject: any, chart: any, y_extra_list: any): void {
    const all_renderer: any[] = [];
    const all_legends: any[] = [];
    let legend, circleObject, source, tooltip, layout;

    if (plot.lines && plot.circles && plot.layouts && plot.lines.length > 0 && plot.circles.length > 0 && plot.layouts.length > 0 && plot.extra_tools.length > 0) {
      _.each(plot.lines, (line, index: string): void => {
        if (line.source.x) {
          line.source.x = line.source.x.map((res: string | number | Date) => {
            return new Date(res).getTime();
          });
        }

        // if (line.source.x) {
        //   // Store timestamps for positioning
        //   line.source.x = line.source.x.map((res: string | number | Date) => {
        //     return new Date(res).getTime();
        //   });
        //
        //   // Format date strings for tooltips
        //   if (!line.source.date_formatted) {
        //     line.source.date_formatted = line.source.x.map((timestamp: number) => {
        //       const dateObj = new Date(timestamp);
        //       const day = String(dateObj.getDate()).padStart(2, '0');
        //       const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        //       const year = dateObj.getFullYear();
        //       return `${day}.${month}.${year}`;
        //     });
        //   }
        // }

        // Add this new block to format date values consistently
        // if (line.source.date && Array.isArray(line.source.date)) {
        //   line.source.date = line.source.date.map((date: any) => {
        //     if (typeof date === 'string' || date instanceof Date) {
        //       const dateObj = new Date(date);
        //       const day = String(dateObj.getDate()).padStart(2, '0');
        //       const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        //       const year = dateObj.getFullYear();
        //       return `${day}.${month}.${year}`;
        //     }
        //     return date;
        //   });
        // }

        source = new Bokeh_v0123.ColumnDataSource({data: line.source,});
        layout = plot.layouts[plot.layouts.length - 1];
        legend = layout.legends[index][0];
        tooltip = plot.extra_tools[index].tooltips;
        lineObject = chart.line({
          x: {
            field: 'x',
          },
          y: {
            field: 'y',
          },
          source: source,
          line_width: line.line_width,
          line_color: line.line_color,
          legend: legend,
        });
        //mysterious here for extra y-axis display
        if (y_extra_list.indexOf(line.y_range_name) != -1)
          lineObject.y_range_name = line.y_range_name;

        circleObject = chart.circle({
          x: {field: 'x',},
          y: {field: 'y',},
          source: source,
          color: line.line_color,
          fill_color: plot.circles[index].fill_color,
          legend: legend,
          size: plot.circles[index].size,
        });
        if (y_extra_list.indexOf(line.y_range_name) != -1)
          circleObject.y_range_name = line.y_range_name;

        all_renderer.push(circleObject);
        all_legends.push(
          new Bokeh_v0123.LegendItem({
            label: legend,
            renderers: [lineObject, circleObject],
          })
        );

        // In the part where you create tooltips
        // if (tooltip && Array.isArray(tooltip)) {
        //   // Replace date field in tooltips with the proper formatted date
        //   tooltip = tooltip.map(tooltipItem => {
        //     if (Array.isArray(tooltipItem) && tooltipItem.length >= 2) {
        //       if (tooltipItem[0].toLowerCase().includes('date')) {
        //         return [tooltipItem[0], '@date_display'];
        //       }
        //     }
        //     return tooltipItem;
        //   });
        // }

        chart.add_tools(
          new Bokeh_v0123.HoverTool({
            renderers: [circleObject],
            tooltips: tooltip
          })
        );
      });
    }
  }

  public createLayout(layout: any, chart: any) {
    for (let type in layout) {
      if (type != 'axis_position' && layout.hasOwnProperty(type)) {
        if (layout[type] != null) {
          switch (type) {
            case 'linear_axis':
              chart = this.createLinearAxis(
                layout[type],
                chart,
                layout['axis_position']
              );
              break;
          }
        }
      }
    }

    return chart;
  }

  public createLinearAxis(
    layout: {
      y_range_name: any;
      axis_label: any;
      axis_label_text_font_size: any;
    },
    chart: { add_layout: (arg0: any, arg1: any) => void },
    position: any
  ) {
    try {
      const axisProps: any = {
        y_range_name: layout.y_range_name,
        axis_label: layout.axis_label
      };

      // Only add font size if specified
      if (layout.axis_label_text_font_size) {
        axisProps['axis_label_text_font_size'] = layout.axis_label_text_font_size;
      }

      const axis = new Bokeh_v0123.LinearAxis(axisProps);

      chart.add_layout(axis, position);
    } catch (error) {
      console.error('Error creating linear axis:', error);
    }

    return chart;
  }
}
